import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, TrendingDown, ShieldAlert, Bot,
  Users, Coins, AlertTriangle, Activity, Clock
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

function MetricCard({ icon: Icon, label, value, change, color = 'blue', delay = 0 }) {
  const colors = {
    blue: 'border-blue-500/20',
    lime: 'border-lime-400/20',
    pink: 'border-pink-400/20',
    orange: 'border-orange-400/20',
  };
  return (
    <motion.div {...fadeUp} transition={{ delay: delay * 0.08, duration: 0.4 }}
      className={`bg-slate-950/60 backdrop-blur-xl border ${colors[color]} rounded-xl p-4 hover:scale-[1.02] transition-transform`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          color === 'blue' ? 'bg-blue-600/20 text-blue-400' :
          color === 'lime' ? 'bg-lime-500/20 text-lime-400' :
          color === 'pink' ? 'bg-pink-500/20 text-pink-400' :
          'bg-orange-500/20 text-orange-400'
        }`}>
          <Icon size={18} />
        </div>
        {change !== undefined && (
          <span className={`text-xs flex items-center gap-0.5 ${change >= 0 ? 'text-lime-400' : 'text-pink-400'}`}>
            {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(change)}%
          </span>
        )}
      </div>
      <p className="text-xs text-slate-400 uppercase tracking-wider font-manrope mb-1">{label}</p>
      <p className="font-barlow text-2xl font-bold text-white">{value}</p>
    </motion.div>
  );
}

const PIE_COLORS = ['#3B82F6', '#bef264', '#ec4899', '#f97316'];

export default function AdminDashboard() {
  const { api } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/economy-health');
      setHealth(res.data);
    } catch (err) {
      console.error('Admin dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const o = health?.overview || {};
  const pieData = Object.entries(health?.decisions_by_type || {}).map(([name, value]) => ({ name: name.replace('_', ' '), value }));

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      {/* Header */}
      <motion.div {...fadeUp} className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight">Economy Health</h1>
          <p className="text-slate-400 text-sm font-manrope mt-0.5">$PLAY Token Economy Overview</p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20">
          <BarChart3 size={14} className="text-blue-400" />
          <span className="text-xs text-blue-400 font-mono uppercase">Admin</span>
        </div>
      </motion.div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Coins} label="Total Issuance" value={`${o.total_issuance || 0} $PLAY`} color="lime" delay={0} />
        <MetricCard icon={TrendingDown} label="Total Sinks" value={`${o.total_sinks || 0} $PLAY`} color="pink" delay={1} />
        <MetricCard icon={Users} label="Total Users" value={o.total_users || 0} color="blue" delay={2} />
        <MetricCard icon={AlertTriangle} label="No-Show Rate" value={`${o.no_show_rate || 0}%`} color="orange" delay={3} />
      </div>

      {/* Second Row: More Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Activity} label="Net Flow" value={`${o.net_flow || 0} $PLAY`} color="blue" delay={4} />
        <MetricCard icon={ShieldAlert} label="Active Fraud Flags" value={o.active_fraud_flags || 0} color="pink" delay={5} />
        <MetricCard icon={Bot} label="Total Decisions" value={o.total_decisions || 0} color="blue" delay={6} />
        <MetricCard icon={AlertTriangle} label="Forfeit Rate" value={`${o.forfeit_rate || 0}%`} color="orange" delay={7} />
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Issuance vs Sinks Chart */}
        <motion.div {...fadeUp} transition={{ delay: 0.3 }}
          className="md:col-span-2 bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
        >
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-4">
            Issuance vs Sinks (7 Day)
          </h3>
          <div className="h-64" data-testid="economy-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={health?.daily_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                  labelStyle={{ color: '#f8fafc' }}
                />
                <Area type="monotone" dataKey="issuance" stroke="#bef264" fill="#bef264" fillOpacity={0.1} strokeWidth={2} name="Issuance" />
                <Area type="monotone" dataKey="sinks" stroke="#ec4899" fill="#ec4899" fillOpacity={0.1} strokeWidth={2} name="Sinks" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Decision Types Pie */}
        <motion.div {...fadeUp} transition={{ delay: 0.35 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
        >
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-4">
            Decision Types
          </h3>
          <div className="h-48" data-testid="decision-pie-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {pieData.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-slate-400 capitalize">{item.name}</span>
                </div>
                <span className="text-white font-mono">{item.value}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Fraud Alerts + Recent Decisions */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Risk Signals */}
        <motion.div {...fadeUp} transition={{ delay: 0.4 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
          data-testid="risk-signals-list"
        >
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert size={16} className="text-pink-400" />
            <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">Fraud Signals</h3>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(health?.risk_signals || []).slice(0, 8).map((rs, i) => (
              <div key={rs.id || i} className="p-3 rounded-lg bg-white/5 border border-white/5 hover:border-pink-500/20 transition-colors">
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
                    rs.severity === 'high' ? 'bg-pink-500/20 text-pink-400' :
                    rs.severity === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>{rs.severity}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    rs.status === 'active' ? 'bg-red-500/10 text-red-400' :
                    rs.status === 'investigating' ? 'bg-yellow-500/10 text-yellow-400' :
                    'bg-slate-500/10 text-slate-400'
                  }`}>{rs.status}</span>
                </div>
                <p className="text-white text-sm font-medium">{rs.user_name || 'Unknown User'}</p>
                <p className="text-slate-400 text-xs capitalize">{rs.signal_type?.replace('_', ' ')}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-slate-500 font-mono">Score: {rs.score}</span>
                  <span className="text-xs text-slate-500">{new Date(rs.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Agent Decisions */}
        <motion.div {...fadeUp} transition={{ delay: 0.45 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
          data-testid="admin-decisions-list"
        >
          <div className="flex items-center gap-2 mb-4">
            <Bot size={16} className="text-blue-400" />
            <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">Agent Decision Feed</h3>
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(health?.recent_decisions || []).slice(0, 8).map((dec, i) => (
              <div key={dec.id || i} className="p-3 rounded-lg bg-white/5 border border-white/5 hover:border-blue-500/20 transition-colors">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
                    dec.decision_type === 'STAKE_QUOTE' ? 'bg-blue-500/20 text-blue-400' :
                    dec.decision_type === 'REWARD_OFFER' ? 'bg-lime-500/20 text-lime-400' :
                    dec.decision_type === 'SUPPRESS' ? 'bg-pink-500/20 text-pink-400' :
                    'bg-orange-500/20 text-orange-400'
                  }`}>{dec.decision_type?.replace('_', ' ')}</span>
                  <span className="text-[10px] text-slate-500 font-mono ml-auto">{dec.agent_name}</span>
                </div>
                <p className="text-slate-300 text-xs">{dec.explanation_internal || dec.explanation_user}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <Clock size={10} className="text-slate-500" />
                  <span className="text-xs text-slate-500">{new Date(dec.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Agent Run Logs */}
      <motion.div {...fadeUp} transition={{ delay: 0.5 }}
        className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
        data-testid="agent-logs-table"
      >
        <div className="flex items-center gap-2 mb-4">
          <Bot size={16} className="text-blue-400" />
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider">Agent Run Logs</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-manrope">
            <thead>
              <tr className="border-b border-white/10 text-slate-400 uppercase tracking-wider">
                <th className="text-left py-2 px-3">Agent</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Cost</th>
                <th className="text-left py-2 px-3">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {(health?.agent_logs || []).slice(0, 10).map((log, i) => (
                <tr key={log.id || i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-2.5 px-3 text-white capitalize">{log.agent_name?.replace('_', ' ')}</td>
                  <td className="py-2.5 px-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      log.status === 'completed' ? 'bg-lime-500/10 text-lime-400' : 'bg-yellow-500/10 text-yellow-400'
                    }`}>{log.status}</span>
                  </td>
                  <td className="py-2.5 px-3 text-slate-400 font-mono">{log.cost_units}</td>
                  <td className="py-2.5 px-3 text-slate-500">{new Date(log.start_ts).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
